import Select, { CSSObjectWithLabel, Props as ReactSelectProps, StylesConfig } from 'react-select';

// Defines the style for the select input box
const controlStyle = (styles: any, { isFocused }: any) => ({
  ...styles,
  borderRadius: 'var(--radius-sm)',
  border: `1px solid ${isFocused ? 'var(--color-input-border-focus)' : 'var(--color-input-border)'}`,
  height: '3.5rem',
  paddingLeft: '.5rem',
  lineHeight: '1.5rem',
  fontSize: '1rem',
  fontWeight: '400',
  outline: 'none',
  boxShadow: 'none',
  '&:hover': {
    border: '1px solid var(--color-input-border-focus)',
  },
});

// Defines how each dropdown option looks and behaves when select menu is open
// isFocused covers both mouse hover and keyboard navigation
const optionStyle = (styles: any, { isSelected, isFocused }: any) => ({
  ...styles,
  paddingLeft: '1.25rem',
  color: 'var(--color-brand-charcoal)',
  backgroundColor: isSelected
    ? 'var(--color-dropdown-bg-active)'
    : isFocused
      ? 'var(--color-dropdown-bg-hover)'
      : 'var(--color-white)',
  '&:active': {
    backgroundColor: 'var(--color-dropdown-bg-active)',
  },
});

// Lifts the portaled menu above the dialog overlay (z-index 10) and the
// nested-modal ModalOverlay (z-index 1001)
const menuPortalStyle = (styles: any) => ({
  ...styles,
  zIndex: 1100,
});

const menuStyle = (styles: CSSObjectWithLabel) => ({
  ...styles,
  borderRadius: 0,
});

/**
 * Common styles shared between ReactSelect and MultiSelect components
 * Provides consistent styling across all select components
 */
export const commonSelectStyles: StylesConfig = {
  control: controlStyle,
  option: optionStyle,
  menu: menuStyle,
  menuPortal: menuPortalStyle,
};

/**
 * Custom single-select component using react-select
 * note: you will need to use a controller from react-hook-form to use this component
 * @param props
 * @returns
 */
export const ReactSelect = (props: ReactSelectProps) => {
  return (
    <Select
      styles={commonSelectStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      {...props}
    />
  );
};

// Extends the base controlStyle and enables height of multi-select to expand to show all options
const multiControlStyle = (styles: any, state: any) => ({
  ...controlStyle(styles, state),
  minHeight: '3.5rem',
  height: 'auto',
});

// Extends the base valueContainerStyle and allows selected tags to wrap to multiple lines
const valueContainerStyle = (styles: any) => ({
  ...styles,
  flexWrap: 'wrap',
  padding: '0.5rem',
});

// Extends the base multiValueStyle and allows selected tags to have a margin and border radius
const multiValueStyle = (styles: any) => ({
  ...styles,
  margin: '0.25rem',
  borderRadius: 0,
  backgroundColor: 'var(--color-brand-cream-500)',
});

// Extends the base multiValueRemoveStyle and allows selected tags to have a hover effect
const multiValueRemoveStyle = (styles: any) => ({
  ...styles,
  color: 'var(--color-brand-cream-700)',
  ':hover': {
    backgroundColor: 'transparent',
    color: 'var(--color-brand-charcoal)',
  },
});

/**
 * Custom multi-select component using react-select
 * note: you will need to use a controller from react-hook-form to use this component
 * @param props
 * @returns
 */
export const MultiSelect = ({ styles, ...props }: ReactSelectProps) => {
  return (
    <Select
      isMulti={true}
      // Portal the menu so it isn't clipped by (or nested inside) a scrolling
      // ancestor such as a dialog body, which makes the tail of a long option
      // list unreachable. Positioning it fixed makes react-select measure
      // against the viewport, so the menu is constrained to the space available
      // (and flips above the control when there isn't enough) instead of
      // overflowing and relying on an ancestor scroll to reach the rest.
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{
        control: multiControlStyle,
        option: optionStyle,
        menu: menuStyle,
        valueContainer: valueContainerStyle,
        multiValue: multiValueStyle,
        multiValueRemove: multiValueRemoveStyle,
        // Callers may replace any of the above, but the portal always needs to
        // clear the dialog overlay, so menuPortal is applied last.
        ...styles,
        menuPortal: menuPortalStyle,
      }}
      {...props}
    />
  );
};

